'use client';

/**
 * API namespace `conectaAdmin` — CONECTA-PR7-MODERATION-REPORTS.
 *
 * Extracted from `frontend/src/lib/api-client/namespaces/conecta.ts` per
 * anti-P-383 (file ≤500L target — `conecta.ts` was 495L + 200L of admin
 * namespace = 696L > target). Mirrors the SPEC-244 IA17 split precedent
 * (`admin-market-reviews.ts` extracted from `admin-market.ts`).
 *
 * Wraps the 5 PR7 admin endpoints under `/api/conecta/admin/*`:
 *   GET   /conecta/admin/categories/pending        → getPendingCategories()
 *   POST  /conecta/admin/categories/:id/approve    → approveCategory()
 *   POST  /conecta/admin/categories/:id/reject     → rejectCategory()
 *   POST  /conecta/admin/categories                → createCategory()      [NEW — TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES]
 *   DELETE /conecta/admin/categories/:id           → deleteCategory()      [NEW — TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES]
 *   GET   /conecta/admin/reports                   → getReports()
 *   POST  /conecta/admin/reports/:id/resolve      → resolveReport()
 *   POST  /conecta/admin/listings/:id/hide        → hideListing()
 *   POST  /conecta/admin/listings/:id/restore     → restoreListing()
 *
 * (PR5 also adds 3 review endpoints — `GET /admin/reviews` + `POST
 * /admin/reviews/:id/hide` + `POST /admin/reviews/:id/restore` — but those
 * are NOT exposed via this FE namespace because the PR7 admin dashboard
 * uses them via a re-export of the review moderation service. Anti-P-347:
 * the BE controller has all 8 endpoints; the FE namespace exposes only the
 * PR7 admin endpoints that the admin dashboard renders.)
 *
 * All methods require `requireAuth + requireAdmin` (gated at the BE router
 * level). The cookie-based auth is handled by `apiFetch` (same as the
 * other namespaces).
 *
 * Anti-§6 (CM-19/CM-15): pure thin wrapper over `apiFetch`. NO business
 * logic. The backend controller (`conecta-admin.controller.ts`) is the
 * source of truth for response shapes — DTOs here mirror them VERBATIM.
 *
 * Anti-P-041: 0 NEW `as any` in this file.
 * Anti-P-042: 0 NEW `console.*`.
 * Anti-P-013: errors propagate as `ApiClientError` (thrown by `apiFetch`).
 * Anti-P-347: VERBATIM mirror of `conectaAuth` namespace pattern
 *             (`'use client'` + `apiFetch<T>` returns `T` + typed DTOs
 *             + named const exports).
 * Anti-P-382: thin wrapper — NO facade, NO service-layer delegation, NO
 *             parallel mechanism on the FE.
 * Anti-P-383: file ≤500L target.
 *
 * Spec ref: docs/SPEC-245-PROFILEIA-CONECTA.md §5.3 (8 admin endpoints),
 *           §8.2 (category approve/reject flow), §12.3 (admin report
 *           resolution + listing hide/restore).
 *           Task ID: CONECTA-PR7-MODERATION-REPORTS.
 *           Task ID: TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES (2026-10-26
 *           — POST + DELETE direct category endpoints + subcategories).
 */

import { apiFetch } from '../core/fetch-client';

// ─── Types (mirror backend conecta-admin.controller.ts + report-helpers.ts) ───

/** Pending category suggestion (admin dashboard item). */
export interface ConectaAdminPendingCategory {
  categoryId: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  vertical: string;
  suggestedByUserId: string | null;
  suggestedAt: string | null;
}

/** Paginated pending categories response (admin GET /admin/categories/pending). */
export interface ConectaPendingCategoriesResponse {
  categories: ConectaAdminPendingCategory[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/** Approved category (returned by approve + reject + create endpoints). */
export interface ConectaAdminCategory {
  categoryId: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  vertical: string;
  /**
   * Subcategories (display labels). TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES
   * — default [] for backward compat with the 49 seed categories (flat).
   */
  subcategories: string[];
  listingCount: number;
}

/**
 * Body for POST /admin/categories (TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES).
 * `vertical` is optional — defaults to 'Otros' on the BE when omitted.
 * `subcategories` is optional — default [].
 */
export interface AdminCreateCategoryBody {
  name: string;
  vertical?: string;
  subcategories?: string[];
}

/**
 * Response for DELETE /admin/categories/:id (TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES).
 * The BE returns `{ ok: true, categoryId }` (wrapped in `{ data: ... }` by
 * the API envelope — `apiFetch` auto-unwraps).
 */
export interface AdminDeleteCategoryResponse {
  ok: true;
  categoryId: string;
}

/**
 * Admin-listed report (admin GET /admin/reports). Includes the reporter's
 * userId + IP hash for the ban action (SPEC §12.3 action_and_ban_reporter).
 * Privacy (SPEC §14.5): admin endpoint only — NOT exposed in public APIs.
 */
export interface ConectaAdminReport {
  reportId: string;
  listingId: string;
  listingUsername: string;
  reporterUserId: string | null;
  reporterIpHash: string | null;
  reason: 'spam' | 'fake_profile' | 'offensive_content' | 'scam' | 'other';
  reasonDetails: string;
  status: 'open' | 'resolved_dismissed' | 'resolved_actioned';
  createdAt: string;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  resolvedByAdminId: string | null;
}

/** Paginated reports response (admin GET /admin/reports). */
export interface ConectaAdminReportsResponse {
  reports: ConectaAdminReport[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/** Body for POST /admin/reports/:id/resolve. */
export interface ResolveReportBody {
  resolution: 'dismiss' | 'action' | 'action_and_ban_reporter';
  notes: string;
}

/** Body for POST /admin/listings/:id/hide. */
export interface AdminHideListingBody {
  reason: string;
}

/** Body for POST /admin/categories/:id/reject. */
export interface AdminRejectCategoryBody {
  reason: string;
}

/** Listing detail returned by admin hide/restore (status='hidden'|'active'). */
export interface ConectaAdminListingView {
  listingId: string;
  username: string;
  displayName: string;
  bio: string;
  photoUrl: string;
  primaryCategoryId: string;
  locationScope: 'global' | 'country' | 'city';
  country: string | null;
  city: string | null;
  isBoosted: boolean;
  boostExpiresAt: string | null;
  reviewCount: number;
  averageRating: number;
  profileSlug: string;
  profileViews: number;
  status: 'active' | 'hidden' | 'paused_by_user' | 'auto_hidden_by_reports';
}

// ─── Admin namespace ──────────────────────────────────────────────────────

export const conectaAdmin = {
  /**
   * GET /conecta/admin/categories/pending — paginated pending category
   * suggestions. Sorted by `suggestedAt` ascending (oldest first — admin
   * should resolve the oldest pending suggestions first to honor the 7-day
   * auto-reject deadline).
   */
  async getPendingCategories(
    page = 1,
    pageSize = 20,
  ): Promise<ConectaPendingCategoriesResponse> {
    return apiFetch<ConectaPendingCategoriesResponse>(
      `/conecta/admin/categories/pending?page=${page}&pageSize=${pageSize}`,
    );
  },

  /**
   * POST /conecta/admin/categories/:id/approve — approve a suggestion.
   * Sets status='approved', reviewedAt=now, reviewedByAdminId. Invalidates
   * `conecta:categories` cache (SPEC §13 rule 20 — visible in filters
   * immediately).
   */
  async approveCategory(categoryId: string): Promise<ConectaAdminCategory> {
    return apiFetch<ConectaAdminCategory>(
      `/conecta/admin/categories/${encodeURIComponent(categoryId)}/approve`,
      { method: 'POST' },
    );
  },

  /**
   * POST /conecta/admin/categories/:id/reject — reject a suggestion with
   * reason. Sets status='rejected', rejectionReason=reason, reviewedAt=now.
   */
  async rejectCategory(
    categoryId: string,
    body: AdminRejectCategoryBody,
  ): Promise<ConectaAdminCategory> {
    return apiFetch<ConectaAdminCategory>(
      `/conecta/admin/categories/${encodeURIComponent(categoryId)}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * POST /conecta/admin/categories — create a category directly (admin,
   * no suggestion flow). TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES.
   *
   * The created category has status='approved' immediately (no pending
   * state). Returns the created category view (with categoryId + slug).
   *
   * Errors (thrown as `ApiClientError`):
   *   - 400 VALIDATION_ERROR (name < 2 chars or vertical not a string)
   *   - 409 CATEGORY_SLUG_EXISTS (slug already in use)
   */
  async createCategory(
    body: AdminCreateCategoryBody,
  ): Promise<ConectaAdminCategory> {
    return apiFetch<ConectaAdminCategory>(`/conecta/admin/categories`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * DELETE /conecta/admin/categories/:id — delete a category (admin).
   * TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES. `categoryId` is the UUID
   * (NOT the MongoDB `_id`) — the BE resolves it via
   * `DirectoryCategory.findOne({ categoryId })`.
   *
   * Errors (thrown as `ApiClientError`):
   *   - 404 CATEGORY_NOT_FOUND (no category matches the UUID)
   *   - 409 CATEGORY_IN_USE (listings still attached — reassign first;
   *     `err.details.listingCount` tells the admin how many)
   */
  async deleteCategory(
    categoryId: string,
  ): Promise<AdminDeleteCategoryResponse> {
    return apiFetch<AdminDeleteCategoryResponse>(
      `/conecta/admin/categories/${encodeURIComponent(categoryId)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * GET /conecta/admin/reports — paginated reports. Status filter:
   * 'open' (default) | 'resolved_dismissed' | 'resolved_actioned' | 'all'.
   */
  async getReports(
    statusFilter: 'open' | 'resolved_dismissed' | 'resolved_actioned' | 'all' = 'open',
    page = 1,
    pageSize = 20,
  ): Promise<ConectaAdminReportsResponse> {
    return apiFetch<ConectaAdminReportsResponse>(
      `/conecta/admin/reports?status=${statusFilter}&page=${page}&pageSize=${pageSize}`,
    );
  },

  /**
   * POST /conecta/admin/reports/:id/resolve — resolve a report.
   * - 'dismiss' → status='resolved_dismissed', listing restored if auto-hidden
   *   AND no other open reports against it.
   * - 'action' → status='resolved_actioned', listing stays hidden.
   * - 'action_and_ban_reporter' → status='resolved_actioned' + reporter IP
   *   banned (30d TTL Redis set).
   */
  async resolveReport(
    reportId: string,
    body: ResolveReportBody,
  ): Promise<ConectaAdminReport> {
    return apiFetch<ConectaAdminReport>(
      `/conecta/admin/reports/${encodeURIComponent(reportId)}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * POST /conecta/admin/listings/:id/hide — admin hide listing manually.
   * Sets status='hidden'. Body: `{ reason: string }` (1-500ch).
   */
  async hideListing(
    listingId: string,
    body: AdminHideListingBody,
  ): Promise<ConectaAdminListingView> {
    return apiFetch<ConectaAdminListingView>(
      `/conecta/admin/listings/${encodeURIComponent(listingId)}/hide`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * POST /conecta/admin/listings/:id/restore — admin restore listing.
   * Sets status='active' (from 'hidden' OR 'auto_hidden_by_reports').
   */
  async restoreListing(listingId: string): Promise<ConectaAdminListingView> {
    return apiFetch<ConectaAdminListingView>(
      `/conecta/admin/listings/${encodeURIComponent(listingId)}/restore`,
      { method: 'POST' },
    );
  },
};
